import { useToast } from '../../../../shared/components/SharedUI';
import { useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCatalogo } from "../hooks/useCatalogo";
import { CuentaNode } from "../components/CuentaNode";
import { CuentaModal } from "../components/CuentaModal";
import styles from "../../invoices/styles/invoice.module.css";

const TIPO_CONFIG = {
  ACTIVO:     { key: "tipoActivo",    icon: "fa-building-columns", color: "#60a5fa" },
  PASIVO:     { key: "tipoPasivo",    icon: "fa-arrow-trend-down", color: "#f87171" },
  PATRIMONIO: { key: "tipoPatrimonio",icon: "fa-shield-halved",    color: "#a78bfa" },
  INGRESO:    { key: "tipoIngreso",   icon: "fa-arrow-trend-up",   color: "#4ade80" },
  GASTO:      { key: "tipoGasto",     icon: "fa-receipt",          color: "#fbbf24" },
};

function contarCuentas(nodo) {
  let total = 1;
  for (const hijo of nodo.subcuentas ?? []) total += contarCuentas(hijo);
  return total;
}

export default function ListCatalogo() {
  const { t } = useTranslation();
  const { showToast, toastEl } = useToast();
  const { arbol, loading, error, crear, editar, activar, refetch, previewEliminar, eliminar } = useCatalogo();

  const [expandedIds,       setExpandedIds]       = useState(new Set());
  const [initializedRoots,  setInitializedRoots]  = useState(false);

  if (arbol.length > 0 && !initializedRoots) {
    setExpandedIds(new Set(arbol.map((r) => r.id)));
    setInitializedRoots(true);
  }

  const handleToggle = useCallback((id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const [modal,        setModal]        = useState(null);
  const [confirmDes,   setConfirmDes]   = useState(null);
  const [elimWarning,  setElimWarning]  = useState(null);
  const [elimLoading,  setElimLoading]  = useState(false);
  const [search,       setSearch]       = useState("");
  const [tipoFiltro,   setTipoFiltro]   = useState("");

  const arbolFiltrado = arbol.filter((r) => !tipoFiltro || r.tipo === tipoFiltro);

  async function handleCrear(data) { await crear(data); }
  async function handleEditar(data) { await editar(modal.cuenta.id, data); }

  async function handleEliminarClick() {
    setElimLoading(true);
    try {
      const data = await previewEliminar(confirmDes.id);
      setElimWarning(data);
    } catch (e) {
      showToast("Error: " + e.message, false);
      setConfirmDes(null);
    } finally {
      setElimLoading(false);
    }
  }

  async function handleEliminarConfirm() {
    setElimLoading(true);
    try {
      await eliminar(confirmDes.id);
      setConfirmDes(null);
      setElimWarning(null);
    } catch (e) {
      showToast("Error al eliminar: " + e.message, false);
    } finally {
      setElimLoading(false);
    }
  }

  return (
    <>
      {toastEl}

      <span className={styles.inactive}>
        <Link to="/">
          <span className={styles.inactive}>{t("common.inicio")} /</span>
        </Link>
        <span className={styles.active}> {t("catalogo.titulo")}</span>
      </span>

      {/* Header */}
      <div style={{
        display: "flex", justifyContent: "space-between",
        alignItems: "flex-start", flexWrap: "wrap", gap: 12, margin: "8px 0 20px",
      }}>
        <div>
          <h2 className="title" style={{ margin: 0, color: "var(--text-primary)" }}>
            {t("catalogo.titulo")}
          </h2>
          <p style={{ margin: "4px 0 0", color: "var(--text-muted)", fontSize: "1rem" }}>
            {t("catalogo.soloDetalle")}
          </p>
        </div>
        <button onClick={refetch}
          style={{
            padding: "8px 14px", background: "var(--hover-bg)",
            border: "1px solid var(--border-card)", borderRadius: 6, color: "var(--text-secondary)",
            cursor: "pointer", fontSize: "0.82rem",
            display: "flex", alignItems: "center", gap: 6, transition: "color 0.2s",
          }}
          onMouseOver={(e) => (e.currentTarget.style.color = "var(--text-primary)")}
          onMouseOut={(e)  => (e.currentTarget.style.color = "var(--text-secondary)")}
        >
          <i className="fa-solid fa-arrows-rotate" /> {t("catalogo.actualizar")}
        </button>
      </div>

      {/* Tarjetas resumen */}
      <div style={{
        display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
        gap: 8, marginBottom: 20,
      }}>
        {arbol.map((raiz) => {
          const cfg   = TIPO_CONFIG[raiz.tipo] || TIPO_CONFIG.ACTIVO;
          const label = t(`catalogo.${cfg.key}`);
          const total = contarCuentas(raiz) - 1;
          const activo = tipoFiltro === raiz.tipo;
          return (
            <button key={raiz.id} type="button"
              onClick={() => setTipoFiltro(activo ? "" : raiz.tipo)}
              style={{
                background: activo ? `${cfg.color}18` : "var(--bg-card)",
                border: `1px solid ${activo ? cfg.color + "55" : "var(--border-card)"}`,
                borderRadius: 8, padding: "12px 14px",
                textAlign: "left", cursor: "pointer", transition: "all 0.15s",
              }}
            >
              <i className={`fa-solid ${cfg.icon}`}
                style={{ color: cfg.color, marginBottom: 6, display: "block" }} />
              <div style={{ color: "var(--text-muted)", fontSize: "0.67rem",
                fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px" }}>
                {label}
              </div>
              <div style={{ color: "var(--text-primary)", fontSize: "0.8rem",
                fontWeight: 700, marginTop: 2, fontFamily: "monospace" }}>
                {raiz.codigo}
              </div>
              <div style={{ color: "var(--text-muted)", fontSize: "0.7rem", marginTop: 2 }}>
                {total} {t("catalogo.cuentas")}
              </div>
            </button>
          );
        })}
      </div>

      {/* Tabla árbol */}
      <div style={{
        background: "var(--bg-card)", border: "1px solid var(--border-card)",
        borderRadius: 8, overflow: "hidden",
        overflowX: "auto",
      }}>
        {/* Barra de búsqueda */}
        <div style={{
          display: "flex", alignItems: "center", gap: 10,
          padding: "10px 14px", borderBottom: "1px solid var(--border-card)",
          background: "var(--hover-bg)",
        }}>
          <i className="fa-solid fa-magnifying-glass" style={{ color: "var(--text-muted)", fontSize: "0.78rem" }} />
          <input type="text"
            placeholder={t("catalogo.buscarPlaceholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ flex: 1, background: "none", border: "none",
              color: "var(--text-primary)", fontSize: "0.85rem", outline: "none" }}
          />
          {tipoFiltro && (
            <button type="button" onClick={() => setTipoFiltro("")}
              style={{ background: "none", border: "none", color: "#f97316", cursor: "pointer", fontSize: "0.78rem" }}>
              <i className="fa-solid fa-xmark" style={{ marginRight: 4 }} />
              {t(`catalogo.${TIPO_CONFIG[tipoFiltro]?.key}`)}
            </button>
          )}
          <span style={{ fontSize: "0.68rem", color: "var(--text-muted)" }}>
            <i className="fa-solid fa-circle" style={{ color: "#f97316", fontSize: "0.45rem", marginRight: 4 }} />
            {t("catalogo.detalleNote")}
          </span>
        </div>

        {/* Cabecera columnas */}
        <div style={{
          display: "grid", gridTemplateColumns: "38px 8px 112px 1fr auto 80px",
          gap: 0, padding: "6px 14px", minWidth: 480,
          borderBottom: "1px solid var(--border-card)", background: "var(--hover-bg)",
        }}>
          {["", "", t("catalogo.codigo"), t("catalogo.nombre"), t("catalogo.tipo"), t("common.acciones")].map((h, i) => (
            <span key={i} style={{ fontSize: "0.65rem", color: "var(--text-muted)",
              fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px" }}>
              {h}
            </span>
          ))}
        </div>

        {loading && (
          <div style={{ textAlign: "center", padding: 40, color: "var(--text-muted)" }}>
            <i className="fa-solid fa-circle-notch fa-spin"
              style={{ color: "#f97316", fontSize: "1.2rem", marginBottom: 8, display: "block" }} />
            {t("catalogo.cargando")}
          </div>
        )}

        {error && (
          <div style={{ padding: 24, color: "#f87171", textAlign: "center" }}>
            <i className="fa-solid fa-triangle-exclamation" style={{ marginRight: 8 }} />
            {error}
          </div>
        )}

        {!loading && !error && (
          <>
            {arbolFiltrado
              .filter((r) =>
                !search ||
                r.codigo.includes(search) ||
                r.nombre.toLowerCase().includes(search.toLowerCase())
              )
              .map((raiz) => (
                <CuentaNode
                  key={raiz.id}
                  cuenta={raiz}
                  depth={0}
                  expandedIds={expandedIds}
                  onToggle={handleToggle}
                  onAgregar={(c) => setModal({ type: "create", padre: c })}
                  onEditar={(c)  => setModal({ type: "edit",   cuenta: c })}
                  onDesactivar={(id, nombre) => { setConfirmDes({ id, nombre }); }}
                  onActivar={(id) => activar(id)}
                />
              ))
            }
            {arbolFiltrado.length === 0 && (
              <div style={{ padding: 40, textAlign: "center", color: "var(--text-muted)" }}>
                {t("catalogo.sinCuentasList")}
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal crear/editar */}
      {modal?.type === "create" && (
        <CuentaModal mode="create" padre={modal.padre} onClose={() => setModal(null)} onSave={handleCrear} />
      )}
      {modal?.type === "edit" && (
        <CuentaModal mode="edit" cuenta={modal.cuenta} onClose={() => setModal(null)} onSave={handleEditar} />
      )}

      {/* Eliminar — paso 1: confirmar intención */}
      {confirmDes && !elimWarning && (
        <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.65)",
          backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center" }}
          onClick={() => { setConfirmDes(null); setElimWarning(null); }}>
          <div style={{ background: "var(--bg-card)", border: "1px solid var(--border-card)",
            borderRadius: 10, padding: 28, width: "min(400px,92vw)" }}
            onClick={e => e.stopPropagation()}>
            <div style={{ textAlign: "center", marginBottom: 18 }}>
              <div style={{ fontSize: "2.2rem", marginBottom: 8 }}>🗑️</div>
              <h3 style={{ color: "var(--text-primary)", margin: 0 }}>¿{t("common.eliminar")} {t("common.nombre").toLowerCase()}?</h3>
              <p style={{ color: "var(--text-secondary)", fontSize: "0.85rem", marginTop: 8 }}>
                {t("common.eliminandoPermanente")}{" "}
                <strong style={{ color: "#f97316" }}>{confirmDes.nombre}</strong>{" "}
                {t("catalogo.eliminarDesc")}
              </p>
            </div>
            <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
              <button onClick={() => { setConfirmDes(null); }}
                style={{ padding: "8px 20px", background: "var(--hover-bg)",
                  border: "1px solid var(--border-card)", borderRadius: 7,
                  color: "var(--text-secondary)", cursor: "pointer" }}>
                {t("common.cancelar")}
              </button>
              <button onClick={handleEliminarClick} disabled={elimLoading}
                style={{ padding: "8px 20px", background: "rgba(239,68,68,0.15)",
                  border: "1px solid rgba(239,68,68,0.4)", borderRadius: 7, color: "#f87171",
                  cursor: "pointer", fontWeight: 700, opacity: elimLoading ? 0.6 : 1 }}>
                {elimLoading ? t("catalogo.verificando") : t("common.confirmar")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Eliminar — paso 2: advertencia detallada */}
      {confirmDes && elimWarning && (
        <div style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.75)",
          backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center" }}
          onClick={() => { setConfirmDes(null); setElimWarning(null); }}>
          <div style={{ background: "var(--bg-card)", border: "1px solid var(--border-card)",
            borderRadius: 10, padding: 24, width: "min(480px,94vw)", maxHeight: "80vh", overflow: "auto" }}
            onClick={e => e.stopPropagation()}>
            <div style={{ textAlign: "center", marginBottom: 16 }}>
              <div style={{ fontSize: "2.2rem", marginBottom: 8 }}>⚠️</div>
              <h3 style={{ color: "var(--text-primary)", margin: 0 }}>{t("catalogo.confirmarEliminar")}</h3>
              <p style={{ color: "#f87171", fontSize: "0.85rem", marginTop: 8, lineHeight: 1.6 }}>
                {elimWarning.advertencia}
              </p>
            </div>
            {elimWarning.cuentas_a_eliminar?.length > 0 && (
              <div style={{ background: "var(--hover-bg)", borderRadius: 6, padding: "10px 14px",
                marginBottom: 16, fontSize: "0.78rem" }}>
                <p style={{ color: "var(--text-muted)", margin: "0 0 6px", fontWeight: 700,
                  textTransform: "uppercase", fontSize: "0.7rem" }}>
                  {t("catalogo.cuentasAEliminar")}
                </p>
                {elimWarning.cuentas_a_eliminar.map((c, i) => (
                  <div key={i} style={{ color: "var(--text-primary)", fontFamily: "monospace", marginBottom: 2 }}>
                    {c.codigo} — {c.nombre}
                  </div>
                ))}
              </div>
            )}
            <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
              <button onClick={() => { setConfirmDes(null); setElimWarning(null); }}
                style={{ padding: "9px 20px", background: "var(--hover-bg)",
                  border: "1px solid var(--border-card)", borderRadius: 7,
                  color: "var(--text-secondary)", cursor: "pointer" }}>
                {t("common.cancelar")}
              </button>
              <button onClick={handleEliminarConfirm} disabled={elimLoading}
                style={{ padding: "9px 20px", background: "rgba(239,68,68,0.15)",
                  border: "1px solid rgba(239,68,68,0.4)", borderRadius: 7, color: "#f87171",
                  cursor: "pointer", fontWeight: 700, opacity: elimLoading ? 0.6 : 1 }}>
                {elimLoading ? t("common.eliminando") : t("catalogo.siEliminarPermanente")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
